import { SwitchProps } from "@mui/material";
import { inject, injectable } from "inversify";
import moment from "moment";
import React from "react";
import { IpfModule } from "../..";
import { Constants } from "../../constants.generated";
import { MessageType } from "../../enums.generated";
import { EventConstants } from "../../EventConstants";
import { doWhen } from "../../functions/do-when";
import { getProperty } from "../../functions/get-property";
import { isFormButton } from "../../functions/is-form-button";
import { isValidDate } from "../../functions/is-valid-date";
import { TYPES } from "../../ioc/types";
import { ConfirmationOption, ConfirmationPromptOptions } from "../../models/app";
import { FormComponentService, LoaderService, TierSelectorService, ValidationService } from "../../services";
import { ServiceBase } from "../../services/ServiceBase";
import { ConfirmationPrompt } from "../components/ConfirmationPrompt";
import { DatePickerOptions, IpfDatePicker } from "../components/DatePicker";
import { MobileNumberInput, MobileNumberOptions } from "../components/MobileNumberInput";
import { SwitchControl } from "../components/SwitchControl";
import { TextInputSearch, TextInputSearchOptions } from "../components/TextInputSearch";
import { ReactDOMProxy } from "../ReactDOMProxy";

/**
 * MUI form component service, used to inject MUI form controls into the UI.
 */
@injectable()
export class MaterialFormComponentService extends ServiceBase implements FormComponentService {

	/**
	 * Initialises a new instance of the MaterialFormComponentService class.
	 * @param ipfModule IPF module, containing contextual information and functions.
	 * @param tierSelectorService Tier selector service, used to apply tier selection controls to the UI.
	 * @param validationService Form validation service.
	 * @param loaderService Page and element loader service.
	 */
	public constructor(
		@inject(TYPES.IpfModule) private ipfModule: IpfModule,
		@inject(TYPES.TierSelectorService) private tierSelectorService: TierSelectorService,
		@inject(TYPES.ValidationService) private validationService: ValidationService,
		@inject(TYPES.LoaderService) private loaderService: LoaderService) {
		super();
	}

	/**
	 * @inheritdoc
	 */
    applyAll(target: Element): void {

		this.applySwitchComponents(target);
		this.applyDatePickers(target);
		this.applyMobileInput(target);
		this.applyTextInputSearchComponents(target);
		this.applyConfirmationPromptComponents(target);
		this.applySubmitLoaders(target);
	}

	//#region Switch

	/**
	 * Applies switch components to target checkbox input elements.
	 * @param target The container element containing the input elements.
	 */
    private applySwitchComponents(target: Element) {

		target.querySelectorAll("input[data-switch]").forEach(input => this.renderSwitch(input));
	}

	/**
	 * Renders a MUI switch component.
	 * @param element The target checkbox input element.
	 */
    private renderSwitch(element: Element) {

		if (!element.parentElement) {
			return;
		}

        const input = element as HTMLInputElement;

        input.classList.add("hide");

        const switchProps: SwitchProps = {
            checked: input.checked
        };

        if (input.getAttribute("data-size") === "small") {

            switchProps.size = "small";
        }

        const props = {
            offLabel: input.getAttribute("data-off-label"),
            onLabel: input.getAttribute("data-on-label"),
            onChange: (checked: boolean) => {

				input.checked = checked;
				input.dispatchEvent(new Event(EventConstants.change));
            },
            switchProps
        };

        const target = document.createElement("span");
		element.parentElement.insertBefore(target, element);

		ReactDOMProxy.renderStyled(
            <SwitchControl {...props} />,
            target
        );
	}

	//#endregion

	//#region Text input search

	/**
	 * Applies text input search components to input elements. Enabling search as you type functionality.
	 * @param target The container element containing the input elements.
	 */
	private applyTextInputSearchComponents(target: Element) {

		target.querySelectorAll("input[data-text-search]").forEach(input => this.renderTextInputSearch(input));
	}

	/**
	 * Renders a text input search component.
	 * @param element The target checkbox input element.
	 */
	private renderTextInputSearch(element: Element) {

		if (!element.parentElement) {
			return;
		}

		const input = element as HTMLInputElement;

		const target = document.createElement("div");
		element.after(target);

		const props: TextInputSearchOptions = {
			input,
			selected: { value: null }
		};

		const self = this;

		input.addEventListener("resultselected", () => {

			const valueField = input.parentElement?.querySelector("[data-text-search-value]") as HTMLInputElement;

			if (valueField) {

				self.setTextInputSearchValueField(props, valueField, input);
			}
		});

		ReactDOMProxy.renderStyled(
			<TextInputSearch {...props} />,
			target
		);
	}

	/**
	 * Set the value of the text input search value field element.
	 * @param options Text input search options.
	 * @param valueField The value field element.
	 * @param inputField The input field element.
	 */
	private setTextInputSearchValueField(options: TextInputSearchOptions, valueField: HTMLInputElement, inputField: HTMLInputElement) {

		const isTierSelector = valueField.hasAttribute("data-tier-selector");

		if (isTierSelector) {

			this.tierSelectorService.setTierSelectorValue(options.selected.value, valueField);
		}
		else {

			valueField.value = getProperty(options.selected.value, inputField.getAttribute("data-value-field") ?? "") ?? "";
		}

		valueField.dispatchEvent(new Event(EventConstants.change));
	}

	//#endregion

	//#region Confirmation prompts

	/**
	 * Applies confirmation prompt components.
	 * Can either be a single configured prompt to be displayed within IPF data or prompts,
	 * that are displayed on click of a button.
	 * @param target The container element containing the input elements.
	 */
	private applyConfirmationPromptComponents(target: Element) {

		// check confirmation prompt configuration with IPF data
		const prompt = this.ipfModule.getData<ConfirmationPromptOptions>(Constants.DataKeyPrompt);
		if (prompt) {

			// configure confirm option handler
			if (prompt.confirmOption) {

				const confirmActionUrl = prompt.confirmOption.actionUrl;
				if (confirmActionUrl) {

					prompt.confirmOption.handler = () => {

						window.location.href = confirmActionUrl;
					}
				}
			}

			// configure cancel option handler
			const cancelActionUrl = prompt.cancelOption?.actionUrl;
			if (prompt.cancelOption && cancelActionUrl) {

				prompt.cancelOption.handler = () => {

					window.location.href = cancelActionUrl;
				}
			}

			this.showConfirmationPrompt(prompt);

			// clear the prompt data
			this.ipfModule.clearData(Constants.DataKeyPrompt);
		}

		// find all elements with conform text and render a confirmation click handler
		target.querySelectorAll("[data-confirm-text]").forEach(element => this.renderButtonConfirmationPrompt(element));
	}

	/**
	 * Renders a confirmation prompt component for a button, shown when the button is clicked.
	 * @param element The target element.
	 */
	private renderButtonConfirmationPrompt(element: Element) {

		if (!element?.parentElement) {
			return;
		}

		// extract prompt properties from the button
		const button = element as HTMLElement;
		const excludeCancelBtn = button.getAttribute("data-confirm-cancel-exclude") === "true";
		const title = button.getAttribute("data-confirm-title") ?? !excludeCancelBtn ? "Confirmation is required" : "";
		let text = button.getAttribute("data-confirm-text");

		if (text) {

			const onConfirm = () => {
				button.setAttribute("data-confirmed", "true");
				button.click();
			};

			const confirmOption = {
				text: button.getAttribute("data-confirm-confirm-label") ?? "Confirm",
				style: "primary",
				handler: onConfirm
			} as ConfirmationOption;

			const cancelOption = !excludeCancelBtn ? {
					text: button.getAttribute("data-confirm-cancel-label") ?? "Cancel",
					style: "secondary"
			} as ConfirmationOption: undefined;

			const renderHtml = button.getAttribute("data-confirm-html-content") === "true";

			if (renderHtml) {
				text = text.replace(/\r\n/g, "<br/>");
			}

			const typeAttr = button.getAttribute("data-confirm-type");
			const type = typeAttr ? MessageType[typeAttr as keyof typeof MessageType] : undefined;

			const options: ConfirmationPromptOptions = {
				title,
				text,
				type,
				confirmOption: confirmOption,
				cancelOption,
				renderTextAsHtml: renderHtml
			};

			// add click handler to show the prompt
			button.onclick = event => {

				// if the prompt was confirmed then remove the attribute and continue as normal
				if (isFormButton(button) && button.getAttribute("data-confirmed") === "true") {

					button.removeAttribute("data-confirmed");
				}
				// otherwise open the prompt
				else if (options.open) {
					options.open();
					event.preventDefault();
				}
			};

			// add a new target container to the buttons parent element
			const target = document.createElement("div");
			element.parentElement.appendChild(target);

			// render prompt the added target container element
			ReactDOMProxy.renderStyled(
				<ConfirmationPrompt options={options} />,
				target
			);
		}
	}

	/**
	 * @inheritdoc
	 */
	public showConfirmationPrompt(options: ConfirmationPromptOptions, targetElement?: Element) {

		// create target element for the prompt
		const target = document.createElement("div");

		// if targetElement not provided, append to body element
		if (!targetElement) {
			document.body.append(target);
		}
		else {
			targetElement.after(target);
		}

		// show prompt once rendered
		options.autoOpen = true;

		// create callback to append content element if it is provided
		const callback = options.content ? () => {
			if (options.content) {
				const ensuredContent = options.content;
				doWhen(() => {

					const dialogContent = document.getElementById("dialog-content");

					if (!dialogContent) {
						return;
					}

					if (ensuredContent.id && dialogContent.querySelector(`#${ensuredContent.id}`)) {
						return;
					}

					dialogContent.appendChild(ensuredContent);
				}, () => document.getElementById("dialog-content") != null);
			}

			// call on open handler if there is one
			if (options.onOpen) {
				options?.onOpen();
			}
		} : () => {

			// call on open handler if there is one
			if (options.onOpen) {
				options?.onOpen();
			}
		};

		// render the prompt to the target with callback function once rendered
		ReactDOMProxy.renderStyled(
			<ConfirmationPrompt options={options} />,
			target,
			callback
		);
	}

	//#endregion

	//#region Button loaders

	/**
	 * Applies submit form loaders, which displays a loading animation component.
	 * Elements must be within a form or have a form attribute referencing a form element.
	 * @param target The container element containing the elements which require a loader component is displayed on click.
	 */
	private applySubmitLoaders(target: Element) {

		target.querySelectorAll("[data-submit-loader]").forEach(element => this.applySubmitLoaderBehaviour(element));
	}

	/**
	 * Applies an individual submit loader component for an element, when clicked.
	 * The element must be within a form or have a form attribute referencing a form element.
	 * @param element The target element.
	 */
	private applySubmitLoaderBehaviour(element: Element) {

		let form: HTMLFormElement | null;

		// get the closest form or the referenced form via form attribute
		if (element instanceof HTMLButtonElement && element.hasAttribute("form")) {

			form = document.querySelector<HTMLFormElement>(`#${element.getAttribute("form")}`);
		}
		else {

			form = element.closest<HTMLFormElement>("form");
		}

		if (!form) {

			return;
		}

		// add a submit event listener to the form
		form.addEventListener("submit", event => {

			// validate the form, if not valid then don't show the loader
			if (form && this.validationService.isValidForm(form) === false) {

				return;
			}

			// if already loading, prevent submit by calling preventDefault
			if (element.getAttribute("data-loading")) {

				event.preventDefault();
				return;
			}

			// get the type of submit loader
			const dataSubmitLoaderValue = element.getAttribute("data-submit-loader");

			// if it's silent then just set the data-loading attribute and return
			if (dataSubmitLoaderValue === Constants.LoaderSilentDisable) {

				element.setAttribute("data-loading", "1");
				return;
			}

			// this wasn't a silent-disable loader so show the form loader

			let loaderTarget: Element | null;

			// check for a selector
			if (dataSubmitLoaderValue) {

				loaderTarget = document.querySelector(dataSubmitLoaderValue);
			}
			else {

				// no selector so use the form as the target
				loaderTarget = form;
			}

			// add loading element to the loader target element
			if (loaderTarget) {

				let loadingContainer = loaderTarget.parentElement;

				if (loadingContainer && loaderTarget.parentElement) {

					if (!loadingContainer.classList.contains("loading-container")) {

						loadingContainer = document.createElement("div");
						loadingContainer.className = "loading-container";
						loaderTarget.parentElement.insertBefore(loadingContainer, loaderTarget);
						loadingContainer.appendChild(loaderTarget);
					}

					if (loadingContainer) {
						loaderTarget.classList.add("form-loading");
						this.loaderService.render(loadingContainer);
					}
				}
			}
		});
	}

	//#endregion

	//#region Date pickers

	/**
	 * Find and applies MUI date pickers within an element.
	 * @param target The container element containing the input elements which require a date picker.
	 */
	private applyDatePickers(target: Element) {

		target.querySelectorAll("[data-date-picker]").forEach(element => this.renderDatePicker(element as HTMLInputElement));
	}

	/**
	 * Renders a date picker component for the input element.
	 * @param input The input element.
	 */
	private renderDatePicker(input: HTMLInputElement) {

		if (!input || !input.parentElement) {
			return;
		}

		const ipf = this.ipfModule;
		const selectedDate = input.value ? moment(input.value).toDate() : undefined;

		// if read only, just display formatted value and don't render date picker component
		if (input.hasAttribute("readonly")) {

			const newInput = document.createElement("input");
			newInput.classList.add("form-control");
			newInput.setAttribute("readonly", "readonly");
			if (selectedDate) {
				newInput.value = moment(selectedDate).format(ipf.context.dateFormat.toUpperCase());
			}
			input.parentElement.insertBefore(newInput, input);
			input.style.display = "none";
			return;
		}

		// build date picker component options
		const target = document.createElement("div");
		input.parentElement.insertBefore(target, input);
		const minDate = input.getAttribute("data-val-min-date-value") ? moment(input.getAttribute("data-val-min-date-value")).toDate() : undefined;
		const maxDate = input.getAttribute("data-val-max-date-value") ? moment(input.getAttribute("data-val-max-date-value")).toDate() : undefined;
		const defaultCalendarDate = input.getAttribute("data-default-calendar-date-is-max-date") == "true" ? maxDate : undefined;
		const placeholder = input.placeholder;
		const props = {
			input,
			selectedDate,
			format: ipf.context.dateFormat,
			minDate,
			maxDate,
			defaultCalendarDate,
			placeholder,
			onChange(date?: Date) {

				// set underlying input field value
				if (date && isValidDate(date)) {
					date.setHours(0, 0, 0, 0);
					if (input.getAttribute("type") === "datetime-local") {
						input.value = moment(date).format("YYYY-MM-DDT00:00");
					}
					else {
						input.value = date.toISOString();
					}
				}
				else {

					input.value = "";
				}

				// dispatch events so any client view models are updated and event handlers called
				input.dispatchEvent(new Event(EventConstants.blur));
				input.dispatchEvent(new Event(EventConstants.change));
			}
		} as DatePickerOptions;

		// add clear handler for IPF search filtering integration
		const clear = () => {
			if (props.clear && !input.value) {
				props.clear();
			}
		}
		input.addEventListener(EventConstants.change_ipffilter, clear);

		// hide the underlying input
		input.classList.add("invisible-input");

		// prevent tab onto this field as it needs to remain 'visible' for validation
		input.tabIndex = 10000;

		// render the date picker component
		ReactDOMProxy.renderStyled(
			<IpfDatePicker options={props} />,
			target,
			() => {
				// requires a check for the control to actually finish rendering
				let muiInput: HTMLInputElement | null;
				doWhen(() => this.applyDatePickerBehaviour(muiInput, input, props), () => (muiInput = target.querySelector<HTMLInputElement>(".MuiInputBase-input")) != null);
			}
		);
	}

	private applyDatePickerBehaviour(muiInput: HTMLInputElement | null, input: HTMLInputElement, props: DatePickerOptions) {

		// TODO: investigate a better fix for this issue, MUI5 date picker seems to not respect the placeholder value passed to it and using inputProps results in format issue.
		if (muiInput) {
			muiInput.placeholder = props.placeholder;
		}
	}

	//#endregion

	//#region Mobile Number Inputs

	/**
	 * Find and applies mobile input components within an element.
	 * @param target The container element containing the input elements which require a mobile input component.
	 */
	private applyMobileInput(target: Element) {

		target.querySelectorAll("[data-mobile-number-input]").forEach(element => this.renderMobileInput(element as HTMLInputElement));
	}

	private renderMobileInput(input: HTMLInputElement) {

		if (!input || !input.parentElement) {
			return;
		}

		const value = input.value;

		// if read only, just display value and don't render mobile input component
		if (input.hasAttribute("readonly")) {

			const newInput = document.createElement("input");
			newInput.classList.add("form-control");
			newInput.setAttribute("readonly", "readonly");
			newInput.value = value;
			input.parentElement.insertBefore(newInput, input);
			input.style.display = "none";
			return;
		}

		// build mobile input options
		const target = document.createElement("div");
		input.parentElement.insertBefore(target, input);
		const placeholder = input.placeholder;
		const props = {
			input,
			value,
			placeholder,
			onChange(number: string) {

				// update input value
				input.value = number;

				// dispatch events so any client view models are updated and event handlers called
				input.dispatchEvent(new Event(EventConstants.blur));
				input.dispatchEvent(new Event(EventConstants.change));
			}
		} as MobileNumberOptions;

		// hide the underlying input
		input.classList.add("invisible-input");

		// prevent tab onto this field as it needs to remain 'visible' for validation
		input.tabIndex = 10000;

		// render the mobile input component
		ReactDOMProxy.render(
			<MobileNumberInput options={props} />,
			target
		);
	}

	//#endregion
}